import { BubbleFormPage, FormHeaderBreadcrumbs } from '@go-client/components/BubbleUI/BubbleForm';
import { upsertSchedule } from '@go-client/lib/api/reservable';
import { BareReserveSchedule } from '@go-shared/types/reserveSchedule';
import React from 'react';
import {
  ScheduleWorkflowPreqs,
  ResRecPathDataBundle,
  RESERVABLE_SCHEDULE_BREADCRUMBS,
} from '../ReservableSchedulePath';
import EnterSchedulePage from '../Pages/EnterSchedulePage';
import SelectExperienceTypePage from '../Pages/SelectExperienceTypePage';
import EnterTicketsPage from '../Pages/EnterTicketsPage';
import EnterPackagesPage from '../Pages/EnterPackagesPage';
import EnterAddonsPage from '../Pages/EnterAddonsPage';
import EnterEventPage from '../Pages/EnterEventPage';
import { BareEventTemplate } from '@go-shared/types/eventTemplate';
import { create } from '@go-client/lib/api/eventTemplate';

export default function buildCreateReservableScheduleWorkflow(
  pages: Array<BubbleFormPage<ResRecPathDataBundle>>,
  preqs: ScheduleWorkflowPreqs
) {
  async function handleSubmitReservableScheduleForm() {
    preqs.setIsBusy(true);

    try {
      const evtToSubmit = (await preqs.eventFormik.submitForm()) as unknown as BareEventTemplate;
      const resToSubmit =
        (await preqs.scheduleFormik.submitForm()) as unknown as BareReserveSchedule;

      const { _id, ...rest } = evtToSubmit;
      const newEventTemplate = await create(rest);

      resToSubmit.eventTemplate = newEventTemplate._id;

      await upsertSchedule(resToSubmit);
      await preqs.onSubmitSchedule?.();
    } finally {
      preqs.setIsBusy(false);
    }
  }

  const breadcrumbs: FormHeaderBreadcrumbs<RESERVABLE_SCHEDULE_BREADCRUMBS>[] = [
    {
      key: 'event',
      label: 'Details',
    },
    {
      key: 'experienceType',
      label: 'Type',
    },
    {
      key: 'tickets',
      label: `${preqs.expTypeLabel}s`,
    },
    {
      key: 'packages',
      label: 'Packages',
    },
    {
      key: 'addons',
      label: 'Addons',
    },
    {
      key: 'schedule',
      label: 'Schedule',
    },
  ];

  pages.push({
    key: 'create-page-1',
    render: actions => (
      <EnterEventPage
        pageIndex={1}
        eventFormik={preqs.eventFormik}
        isBusy={preqs.isBusy}
        setIsBusy={preqs.setIsBusy}
        onGoForward={actions.goForward}
        onClose={preqs.onClose}
        libraryLocked={preqs.libraryLocked}
        breadcrumbs={breadcrumbs}
        forwardTitle="Next: Select Type"
      />
    ),
  });

  pages.push({
    key: 'create-page-2',
    render: actions => (
      <SelectExperienceTypePage
        pageIndex={2}
        mode={preqs.mode}
        eventFormik={preqs.eventFormik}
        isBusy={preqs.isBusy}
        setIsBusy={preqs.setIsBusy}
        onGoForward={actions.goForward}
        onGoBack={actions.goBack}
        onClose={preqs.onClose}
        libraryLocked={preqs.libraryLocked}
        breadcrumbs={breadcrumbs}
        forwardTitle={`Next: Add ${preqs.expTypeLabel}s`}
      />
    ),
  });

  pages.push({
    key: 'create-page-3',
    render: actions => (
      <EnterTicketsPage
        pageIndex={3}
        eventFormik={preqs.eventFormik}
        experienceType={preqs.experienceType}
        expTypeLabel={preqs.expTypeLabel}
        isBusy={preqs.isBusy}
        setIsBusy={preqs.setIsBusy}
        onGoForward={actions.goForward}
        onGoBack={actions.goBack}
        onClose={preqs.onClose}
        libraryLocked={preqs.libraryLocked}
        breadcrumbs={breadcrumbs}
        forwardTitle="Next: Add Packages"
      />
    ),
  });

  pages.push({
    key: 'create-page-4',
    render: actions => (
      <EnterPackagesPage
        pageIndex={4}
        eventFormik={preqs.eventFormik}
        isBusy={preqs.isBusy}
        setIsBusy={preqs.setIsBusy}
        onGoForward={actions.goForward}
        onGoBack={actions.goBack}
        onClose={preqs.onClose}
        libraryLocked={preqs.libraryLocked}
        breadcrumbs={breadcrumbs}
        forwardTitle="Next: Add Addons"
      />
    ),
  });

  pages.push({
    key: 'create-page-5',
    render: actions => (
      <EnterAddonsPage
        pageIndex={5}
        eventFormik={preqs.eventFormik}
        isBusy={preqs.isBusy}
        setIsBusy={preqs.setIsBusy}
        onGoForward={actions.goForward}
        onGoBack={actions.goBack}
        onClose={preqs.onClose}
        libraryLocked={preqs.libraryLocked}
        breadcrumbs={breadcrumbs}
        forwardTitle="Next: Enter Schedules"
      />
    ),
  });

  pages.push({
    key: 'create-page-6',
    render: (actions, res) =>
      res && (
        <EnterSchedulePage
          pageIndex={6}
          eventFormik={preqs.eventFormik}
          scheduleFormik={preqs.scheduleFormik}
          experienceType={preqs.experienceType}
          isBusy={preqs.isBusy}
          setIsBusy={preqs.setIsBusy}
          onGoForward={handleSubmitReservableScheduleForm}
          scheduleLocked={preqs.scheduleLocked}
          onGoBack={actions.goBack}
          onClose={preqs.onClose}
          users={res.users}
          venueTemplates={res.venueTemplates}
          breadcrumbs={breadcrumbs}
          forwardTitle="Finish: Create Experience*"
        />
      ),
  });
}
